import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import { healthCyclesAPI } from '../../services/api'
import CycleStepProgress from './CycleStepProgress'
import CycleDraftView from './CycleDraftView'
import HealthReportTab from './HealthReportTab'
import NutritionReportTab from './NutritionReportTab'
import FollowUpTab from './FollowUpTab'
import CycleDocumentsTab from './documents/CycleDocumentsTab'
import CycleChatPanel from './CycleChatPanel'
import CycleHistory from './CycleHistory'
import CycleVersionCompare from './CycleVersionCompare'
import FinalizeDialog from './FinalizeDialog'
import SupplementaryTestsSection from './SupplementaryTestsSection'
import RevisionChangeSummary from './RevisionChangeSummary'
import TestsToRequestSection from './TestsToRequestSection'
import { errorCode, formatDate, isCycleActive } from './cycleStatus'

const POLL_MS = 3000

// VH-228/223: a report tab renders once its step completed; older versions carry their own payload.
const reportReady = (step, viewingCurrent) => !viewingCurrent || step?.status === 'completed'

// VH-227: one cycle — step progress while the runner works, the draft (or a
// selected version), the correction chat, finalize, and the change history.
export function CycleView({ cycleId: cycleIdProp }) {
  const params = useParams()
  const [searchParams] = useSearchParams()
  const cycleId = Number(cycleIdProp ?? params.id)
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [selectedNumber, setSelectedNumber] = useState(null)
  const [finalizeOpen, setFinalizeOpen] = useState(false)
  const [actionError, setActionError] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const cycleQuery = useQuery({
    queryKey: ['healthCycle', cycleId],
    queryFn: () => healthCyclesAPI.get(cycleId),
    select: (response) => response.data,
    enabled: !!cycleId,
    refetchInterval: (query) => (isCycleActive(query.state.data?.data) ? POLL_MS : false),
  })
  const cycle = cycleQuery.data
  const active = isCycleActive(cycle)
  const versionNumber = selectedNumber ?? cycle?.current_version_number

  // Follow the current version when a confirmed change creates a new one.
  useEffect(() => {
    setSelectedNumber(null)
  }, [cycle?.current_version_number])

  const versionQuery = useQuery({
    queryKey: ['healthCycleVersion', cycleId, versionNumber],
    queryFn: () => healthCyclesAPI.getVersion(cycleId, versionNumber),
    select: (response) => response.data,
    enabled: !!cycleId && !!versionNumber,
    refetchInterval: active && !selectedNumber ? POLL_MS : false,
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['healthCycle', cycleId] })
    queryClient.invalidateQueries({ queryKey: ['healthCycles'] })
    queryClient.invalidateQueries({ queryKey: ['healthCycleVersion', cycleId] })
  }
  const failWith = (err) => setActionError({ code: errorCode(err),
    message: t(`healthCycle.errors.${errorCode(err)}`, t('healthCycle.errors.generic')) })

  // "Steps are still running" describes a moment, not a state: once the steps
  // finish it is false, so it must go without waiting for another click.
  useEffect(() => {
    if (!active) setActionError((current) => (current?.code === 'cycle_running' ? null : current))
  }, [active])

  const retryMutation = useMutation({
    mutationFn: (step) => healthCyclesAPI.retryStep(cycleId, step),
    onSuccess: () => { setActionError(null); invalidate() },
    onError: failWith,
  })
  const rerunMutation = useMutation({
    mutationFn: () => healthCyclesAPI.rerun(cycleId),
    onSuccess: () => { setActionError(null); invalidate() },
    onError: failWith,
  })
  // VH-246: the runner died mid-cycle; the server confirms it is stalled before resuming.
  const resumeMutation = useMutation({
    mutationFn: () => healthCyclesAPI.resume(cycleId),
    onSuccess: () => { setActionError(null); invalidate() },
    onError: failWith,
  })
  const finalizeMutation = useMutation({
    mutationFn: () => healthCyclesAPI.finalize(cycleId),
    onSuccess: () => {
      setFinalizeOpen(false)
      setActionError(null)
      invalidate()
      queryClient.invalidateQueries({ queryKey: ['healthCycleMessages', cycleId] })
      queryClient.invalidateQueries({ queryKey: ['healthCycleTestChecklist', cycleId] })
      queryClient.invalidateQueries({ queryKey: ['healthCycleChangeSummary', cycleId] })
    },
    onError: failWith,
  })
  const deleteMutation = useMutation({
    mutationFn: () => healthCyclesAPI.remove(cycleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['healthCycles'] })
      navigate('/health-cycle')
    },
    onError: failWith,
  })

  if (cycleQuery.isLoading) {
    return <div className="p-6">{t('common.loading')}</div>
  }
  if (cycleQuery.isError || !cycle) {
    return (
      <div className="p-6">
        <p className="text-sm text-red-600">{t('healthCycle.errors.notFound')}</p>
        <Link to="/health-cycle" className="text-primary-600 text-sm">{t('common.back')}</Link>
      </div>
    )
  }

  const finalized = cycle.status === 'finalized'
  // A change confirmed after finalizing creates a new, unfinalized revision; it must be finalizable again.
  const canFinalize = !finalized || (cycle.current_version_number != null && cycle.current_version_number !== cycle.baseline_version_number)
  const viewingCurrent = !selectedNumber || selectedNumber === cycle.current_version_number
  // VH-233: an unfinalized revision on a finalized cycle (chat change or supplementary test)
  const openRevision = finalized && cycle.current_version_number != null && cycle.baseline_version_number != null
    && cycle.current_version_number !== cycle.baseline_version_number

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-4">
      <Link to="/health-cycle" className="inline-flex items-center text-primary-600 hover:text-primary-700 text-sm">
        <ArrowLeftIcon className="h-4 w-4 mr-1" />
        {t('healthCycle.backToCycles')}
      </Link>

      <header className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('healthCycle.list.bloodTestOf', { date: formatDate(cycle.blood_test_date, i18n.language) })}
          </h1>
          <p className="text-sm text-gray-600">
            {t(`healthCycle.status.${cycle.status}`, cycle.status)}
            {cycle.finalized_at && ` · ${t('healthCycle.list.finalizedOn', { date: formatDate(cycle.finalized_at, i18n.language) })}`}
            {cycle.deferred && ` · ${t('healthCycle.deferred')}`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {cycle.versions.length > 0 && (
            <label className="text-sm text-gray-700 flex items-center gap-2">
              {t('healthCycle.versions.picker')}
              <select
                className="text-sm"
                value={versionNumber ?? ''}
                onChange={(e) => setSelectedNumber(Number(e.target.value))}
              >
                {cycle.versions.map((v) => (
                  <option key={v.number} value={v.number}>
                    {t('healthCycle.versions.label', { number: v.number })}
                    {v.number === cycle.baseline_version_number ? ` (${t('healthCycle.versions.baseline')})` : ''}
                    {v.number === cycle.current_version_number ? ` (${t('healthCycle.versions.current')})` : ''}
                  </option>
                ))}
              </select>
            </label>
          )}
          <button type="button" className="btn-outline text-sm" disabled={active || rerunMutation.isPending} onClick={() => rerunMutation.mutate()}>
            {t('healthCycle.actions.rerun')}
          </button>
          {canFinalize && (
            <button type="button" className="btn-primary text-sm" disabled={active} onClick={() => setFinalizeOpen(true)}>
              {finalized ? t('healthCycle.actions.finalizeRevision') : t('healthCycle.actions.finalize')}
            </button>
          )}
          <button type="button" className="text-sm text-red-600 hover:text-red-800 px-2" onClick={() => setConfirmDelete(true)}>
            {t('common.delete')}
          </button>
        </div>
      </header>

      {confirmDelete && (
        <div className="border border-red-200 bg-red-50 rounded-md p-3 text-sm" role="alertdialog">
          <p className="text-red-800">{t('healthCycle.actions.deleteWarning')}</p>
          <div className="flex gap-2 mt-2">
            <button type="button" className="btn-outline text-xs" onClick={() => setConfirmDelete(false)}>{t('common.cancel')}</button>
            <button type="button" className="btn-danger text-xs" disabled={deleteMutation.isPending} onClick={() => deleteMutation.mutate()}>
              {t('healthCycle.actions.deleteConfirm')}
            </button>
          </div>
        </div>
      )}

      {actionError && <p className="text-sm text-red-600" role="alert">{actionError.message}</p>}

      {cycle.stalled && (
        <section className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
          role="status" data-testid="stalled-banner">
          <p className="text-sm text-amber-900">{t('healthCycle.stalled.message')}</p>
          <button type="button" className="btn-primary text-sm shrink-0" disabled={resumeMutation.isPending}
            onClick={() => resumeMutation.mutate()}>
            {t('healthCycle.stalled.resume')}
          </button>
        </section>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          {openRevision && !active && (
            <section className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="revision-banner">
              <p className="text-sm text-amber-900">
                {t('healthCycle.changeSummary.banner', { number: cycle.current_version_number, baseline: cycle.baseline_version_number })}
              </p>
              <RevisionChangeSummary cycleId={cycleId} versionNumber={cycle.current_version_number} />
            </section>
          )}
          <SupplementaryTestsSection cycleId={cycleId} active={active}
            payload={viewingCurrent && !active ? versionQuery.data?.payload : null} />
          <TestsToRequestSection cycleId={cycleId} cycleDate={cycle.blood_test_date} />
          <CycleDraftView
            version={versionQuery.data}
            steps={viewingCurrent ? cycle.steps : []}
            initialTab={searchParams.get('tab') ?? 'overview'}
            reportRenderers={{
              followup: ({ step }) => (!viewingCurrent || (step && !['pending', 'running'].includes(step.status))
                ? <FollowUpTab cycleId={cycleId} versionNumber={viewingCurrent ? null : versionNumber} />
                : null),
              health_report: ({ step, openTab }) => (reportReady(step, viewingCurrent)
                ? <HealthReportTab cycleId={cycleId} versionNumber={versionNumber} onOpenNutrition={() => openTab('nutrition_report')} />
                : null),
              nutrition_report: ({ step }) => (reportReady(step, viewingCurrent)
                ? <NutritionReportTab cycleId={cycleId} versionNumber={versionNumber} hasBaseline={cycle.baseline_version_number != null} />
                : null),
              documents: () => <CycleDocumentsTab cycleId={cycleId} />,
            }}
          />
          {cycle.versions.length > 1 && <CycleVersionCompare cycleId={cycleId} versions={cycle.versions} />}
          <CycleHistory cycleId={cycleId} />
        </div>
        <div className="space-y-4">
          <CycleStepProgress
            steps={cycle.steps}
            onRetry={(step) => retryMutation.mutate(step)}
            retrying={retryMutation.isPending}
          />
          <CycleChatPanel cycleId={cycleId} cycleRunning={active} />
        </div>
      </div>

      <FinalizeDialog
        cycleId={cycleId}
        open={finalizeOpen}
        onClose={() => setFinalizeOpen(false)}
        onConfirm={() => finalizeMutation.mutate()}
        busy={finalizeMutation.isPending}
        error={finalizeOpen ? actionError?.message : null}
      />
    </div>
  )
}

export default CycleView
